import { ArrowRightOutlined, ToTopOutlined } from '@ant-design/icons'
import { useFormatter } from '@api/useFormatter'
import { useDirectiveContext } from '@components/Reports/DispatchDirective/DirectiveContext'
import { generateChartData } from '@components/Reports/DispatchDirective/util'
import { addCommasToNumber, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import api from '@utils/api'
import { Button } from 'antd'
import { useEffect, useState } from 'react'

import { IntradayProgressBar } from '../IntradayProgressBar'
import { HourlyChart } from '../Selected/HourlyChart'

export function AllStatus({ dailyDirectives, isDailyLoading }) {
  const { selectedDate, viewingPlanned } = useDirectiveContext()
  const [directiveTotals, setDirectiveTotals] = useState({})
  const [directiveChartData, setDirectiveChartData] = useState()
  const [chartLoading, setChartLoading] = useState(true)

  useEffect(() => {
    const totals = {
      totalLoaded: 0,
      totalScheduled: 0,
      totalPlanned: 0,
      totalVolume: 0,
      totalCarrier: 0,
      totalUnder: 0,
      totalOver: 0,
    }
    dailyDirectives.forEach((directive) => {
      totals.totalLoaded += directive.loaded
      totals.totalScheduled += directive.scheduled
      if (viewingPlanned) {
        totals.totalPlanned += directive.planned
      }

      totals.totalCarrier += directive.carrier
      if (directive.delta_to_directive < 0) {
        totals.totalUnder += Math.abs(viewingPlanned ? directive.delta_to_directive : directive.delta_without_planned)
      } else if (directive.delta_to_directive > 0) {
        totals.totalOver += viewingPlanned ? directive.delta_to_directive : directive.delta_without_planned
      }
    })
    totals.totalVolume = totals.totalLoaded + totals.totalPlanned + totals.totalScheduled + totals.totalCarrier
    setDirectiveTotals(totals)
    const superQuery = {
      as_of: selectedDate.toISOString(),
      curve_group_ids: dailyDirectives.map((directive) => directive.curve_group_id),
      curve_ids: dailyDirectives.flatMap((directive) => directive.curve_ids),
      contract_terminal_product_suppliers: dailyDirectives.flatMap(
        (directive) => directive.contract_terminal_product_suppliers
      ),
    }
  }, [dailyDirectives, viewingPlanned])

  return (
    <>
      <VolumeStatus directiveTotals={directiveTotals} isLoading={isDailyLoading} />
      <Horizontal className='mb-3'>
        <DifferenceCard value={directiveTotals.totalUnder} />
        <DifferenceCard value={directiveTotals.totalOver} isOver />
      </Horizontal>
      <Vertical flex={3} alignItems='center' justifyContent='center'>
        <Texto appearance='medium' weight='bold' className='mb-3' category='heading'>
          Select a directive to view charts
        </Texto>
      </Vertical>
    </>
  )
}
function VolumeStatus({ directiveTotals, isLoading }) {
  const { viewingPlanned } = useDirectiveContext()
  return (
    <Vertical flex={1} border='all' borderRadius={3} background='bg-2' className='mb-4 px-4 py-3'>
      <Texto appearance='medium' weight='bold'>
        INTRADAY VOLUME STATUS
      </Texto>
      <Horizontal verticalCenter className='pr-3 pt-2' flex={1}>
        <IntradayProgressBar
          isLoading={isLoading}
          barHeight={30}
          directive={{
            directive_max: directiveTotals.totalVolume,
            loaded: directiveTotals.totalLoaded,
            scheduled: directiveTotals.totalScheduled,
            planned: directiveTotals.totalPlanned,
            directive_min: directiveTotals.totalMins,
            carrier: directiveTotals.totalCarrier,
          }}
          showTargets={false}
          addPadding={false}
          showPlanned={viewingPlanned}
        />
      </Horizontal>
      <Horizontal>
        <Vertical flex={2}>
          <Texto weight={600} appearance='medium'>
            Directive Order Volume
          </Texto>
          <Texto weight='bold' category='h3'>
            {addCommasToNumber(directiveTotals.totalVolume, 0)}
          </Texto>
        </Vertical>
        <Vertical flex={1} verticalCenter>
          <div className='mx-3' style={{ width: 2, height: 25, background: 'var(--gray-300)' }} />
        </Vertical>

        <Vertical verticalCenter flex={2}>
          <Horizontal>
            <Vertical verticalCenter style={{ maxWidth: 30 }}>
              <div
                className='mx-3'
                style={{ width: 10, height: 10, borderRadius: 20, background: 'var(--theme-color-1)' }}
              />
            </Vertical>
            <Vertical>
              <Texto weight={600} appearance='medium'>
                Total Loaded
              </Texto>
              <Texto weight='bold' category='h4' appearance='primary'>
                {addCommasToNumber(directiveTotals.totalLoaded, 0)}
              </Texto>
            </Vertical>
          </Horizontal>
        </Vertical>
        <Vertical verticalCenter flex={2}>
          <Horizontal>
            <Vertical verticalCenter style={{ maxWidth: 30 }}>
              <div
                className='mx-3'
                style={{ width: 10, height: 10, borderRadius: 20, background: 'var(--theme-color-4)' }}
              />
            </Vertical>
            <Vertical>
              <Texto weight={600} appearance='medium'>
                Carrier Volume
              </Texto>
              <Texto weight='bold' category='h4' style={{ color: 'var(--theme-color-4)' }}>
                {addCommasToNumber(directiveTotals.totalCarrier, 0)}
              </Texto>
            </Vertical>
          </Horizontal>
        </Vertical>
        <Vertical verticalCenter flex={2}>
          <Horizontal>
            <Vertical verticalCenter style={{ maxWidth: 30 }}>
              <div
                className='mx-3'
                style={{ width: 10, height: 10, borderRadius: 20, background: 'var(--theme-color-2)' }}
              />
            </Vertical>
            <Vertical>
              <Texto weight={600} appearance='medium'>
                Scheduled
              </Texto>
              <Texto weight={600} category='h4' appearance='secondary'>
                {addCommasToNumber(directiveTotals.totalScheduled, 0)}
              </Texto>
            </Vertical>
          </Horizontal>
        </Vertical>

        {viewingPlanned && (
          <Vertical verticalCenter flex={2}>
            <Horizontal>
              <Vertical verticalCenter style={{ maxWidth: 30 }}>
                <div
                  className='mx-3'
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 20,
                    background:
                      'repeating-linear-gradient(45deg,var(--theme-info), var(--theme-info) 2px, var(--gray-100) 2px, var(--gray-100) 4px)',
                  }}
                />
              </Vertical>

              <Vertical>
                <Texto weight={600} appearance='medium'>
                  Planned
                </Texto>
                <Texto weight={600} category='h4' appearance='medium'>
                  {addCommasToNumber(directiveTotals.totalPlanned, 0)}
                </Texto>
              </Vertical>
            </Horizontal>
          </Vertical>
        )}
      </Horizontal>
    </Vertical>
  )
}
function DifferenceCard({ isOver, value }) {
  const { setFilters } = useDirectiveContext()
  const { formatVolume } = useFormatter()
  const formattedUnit = formatVolume(null, false)
  return (
    <Vertical border='all' borderRadius={3} flex={1} background='bg-2' className='pt-3 px-4 mr-3'>
      <Horizontal className='pb-2' border='bottom'>
        <Vertical flex={2}>
          <Texto weight={600} appearance='medium'>
            Today’s {formattedUnit} {isOver ? 'Over' : 'Under'} Directives
          </Texto>
          <Texto category='h3'>{addCommasToNumber(value, 0)}</Texto>
        </Vertical>
        <Vertical alignItems='flex-end'>
          <Texto category='h2' appearance='error'>
            <ToTopOutlined style={{ transform: `rotate(${isOver ? 0 : 180}deg)` }} />
          </Texto>
        </Vertical>
      </Horizontal>
      <Horizontal verticalCenter>
        <Vertical flex={1}>
          <Button
            onClick={() => setFilters((filters) => ({ ...filters, status: isOver ? 'over' : 'under' }))}
            style={{ paddingLeft: 0, textAlign: 'left' }}
            type='link'
          >
            View {isOver ? 'Over' : 'Under'} Directives
          </Button>
        </Vertical>
        <Vertical verticalCenter alignItems='flex-end'>
          <Texto appearance='secondary'>
            <ArrowRightOutlined />
          </Texto>
        </Vertical>
      </Horizontal>
    </Vertical>
  )
}
